import { SearchProvider } from '@faststore/sdk'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import ProductGallery from 'src/components/sections/ProductGalleryV2'
import { ITEMS_PER_PAGE } from 'src/constants'
import { applySearchState } from 'src/sdk/search/state'
import RenderPageSections from 'src/components/cms/RenderedPageSections'
import { RecommendationProvider } from 'src/components/restructure/product/contexts/Recommendations/recommendations-context'
import type { InfluencersSectionData } from 'src/components/sections/ProductGalleryV2/ProductGalleryv3'
import type { ServerData } from 'src/utils/plp/getCollectionTemplateServerData'
import { ItemListJsonLd } from 'src/components/restructure/product/sections/ItemListJsonLd/ItemListJsonLd'
import { MusePlpEntryPoint } from 'src/components/muse/MusePlpEntryPoint'
import {
  splitSeoFaqSections,
  withListingHeading,
} from 'src/utils/plp/partitionPlpSections'
import { ListingH1 } from 'src/components/plp/ListingH1'

type SiteMetadata = {
  siteMetadata: {
    title: string | null
    titleTemplate: string | null
    description: string | null
    siteUrl: string | null
    ogImage: string | null
  } | null
} | null

export type CollectionPLPTemplateProps = {
  serverData: ServerData['serverData']
  location: { pathname: string; search: string }
  data: { site: SiteMetadata }
}

export function CollectionPLPTemplate(props: CollectionPLPTemplateProps) {
  const {
    serverData: {
      searchParams,
      searchResult,
      cmsData,
      collection,
      sphereContents,
    },
    location: { pathname, search },
    data: { site },
  } = props

  const listingName = collection.collectionName ?? ''
  const sections = withListingHeading(cmsData?.sections ?? [], listingName)
  const { aboveGallery, belowGallery } = splitSeoFaqSections(sections)
  const hasActiveTopBanner = sections.some(
    (section) => section.data?.showListingH1
  )

  const influencersSectionData = sections.find(
    (section) => section.name === '[Coleção 2.0] Influencers'
  )?.data

  const influencersSection: InfluencersSectionData | undefined =
    influencersSectionData as InfluencersSectionData | undefined

  const title = cmsData?.seo?.siteMetadataWithSlug?.title

  const description = cmsData?.seo?.siteMetadataWithSlug?.description

  const EVENT_API_BASE_PATH = 'https://sp.vtex.com'

  if (!searchParams) {
    return null
  }

  const hasDYShelfOnSections = sections.some(
    (section) => section.name === 'Vitrine DY [Colecao]'
  )

  const hasPictosOnSections = sections.some((section) =>
    section.name.includes('Pictos')
  )

  return (
    <SearchProvider
      onChange={applySearchState}
      itemsPerPage={ITEMS_PER_PAGE}
      {...searchParams}
    >
      {/* SEO */}
      <GatsbySeo
        language="pt-br"
        title={title ?? ''}
        description={description ?? ''}
        titleTemplate={site?.siteMetadata?.titleTemplate ?? ''}
        canonical={`${site?.siteMetadata?.siteUrl}${pathname}${search}`}
        openGraph={{
          images: [{ url: site?.siteMetadata?.ogImage ?? '' }],
          type: 'website',
          title,
          description,
        }}
        linkTags={[
          {
            rel: 'preconnect',
            href: EVENT_API_BASE_PATH,
          },
          {
            rel: 'preconnect',
            href: 'https://decathlonproqa.vteximg.com.br',
          },
          {
            rel: 'preconnect',
            href: 'https://decathlonstore.vtexassets.com',
          },
          {
            rel: 'dns-prefetch',
            href: EVENT_API_BASE_PATH,
          },
          {
            rel: 'dns-prefetch',
            href: 'https://decathlonproqa.vteximg.com.br',
          },
          {
            rel: 'dns-prefetch',
            href: 'https://decathlonstore.vtexassets.com',
          },
        ]}
      />

      <ItemListJsonLd
        products={searchResult?.products}
        siteUrl={site?.siteMetadata?.siteUrl}
        pageUrl={`${pathname}${search}`}
        name={title ?? collection.collectionName}
        description={description}
        currentPage={searchParams?.page}
        itemsPerPage={ITEMS_PER_PAGE}
      />

      <RecommendationProvider
        pageName="collection"
        engines={hasDYShelfOnSections ? ['dy'] : []}
      >
        {aboveGallery.length > 0 ? (
          <RenderPageSections
            sections={aboveGallery as Array<{ name: string; data: any }>}
          />
        ) : null}
      </RecommendationProvider>

      {!hasActiveTopBanner && listingName ? (
        <ListingH1 text={listingName} variant="visible" />
      ) : null}

      <ProductGallery
        title={listingName}
        type="collection"
        searchResult={searchResult}
        dataSports={cmsData}
        sphereContents={sphereContents}
        hasPictosOnSections={hasPictosOnSections}
        influencersData={influencersSection}
        alignWithListingHeading={!hasActiveTopBanner && Boolean(listingName)}
      />
      <MusePlpEntryPoint />

      {belowGallery.length > 0 ? (
        <RenderPageSections
          sections={belowGallery as Array<{ name: string; data: any }>}
        />
      ) : null}
    </SearchProvider>
  )
}

export default CollectionPLPTemplate
